/**
 * Job Management API Service
 * 
 * This service provides functions for interacting with the job management API endpoints.
 * It handles job CRUD operations and job logs retrieval with proper error handling.
 */
import apiClient from './apiClient';

interface Job {
  id: string;
  name: string;
  function_name: string;
  description?: string;
  cron_expression?: string;
  is_active: boolean;
  last_run?: string;
  created_at: string;
  updated_at: string;
}

interface JobFormData {
  name: string;
  function_name: string;
  description?: string;
  cron_expression?: string;
  is_active: boolean;
}

interface JobRun {
  id: string;
  job_id: string;
  scheduled_time: string;
  start_time: string;
  end_time?: string;
  duration?: number;
  status: string;
  error_message?: string;
  created_at: string;
}

interface JobLogEntry {
  id: string;
  log_level: string;
  message: string;
  timestamp: string;
}

interface JobLogDetail {
  job_run_id: string;
  logs: JobLogEntry[];
}

/**
 * Job Management API functions
 */
export const jobApi = {
  /**
   * Get all jobs
   */
  getAllJobs: async (): Promise<Job[]> => {
    const response = await apiClient.get<Job[]>('/api/v2/web/jobs');
    return response.data;
  },

  /**
   * Get a specific job by ID
   */
  getJobById: async (jobId: string): Promise<Job> => {
    const response = await apiClient.get<Job>(`/api/v2/web/jobs/${jobId}`);
    return response.data;
  },

  /**
   * Create a new job
   */
  createJob: async (jobData: JobFormData): Promise<Job> => {
    const response = await apiClient.post<Job>('/api/v2/web/jobs', jobData);
    return response.data;
  },

  /**
   * Update an existing job
   */
  updateJob: async (jobId: string, jobData: JobFormData): Promise<Job> => {
    const response = await apiClient.put<Job>(`/api/v2/web/jobs/${jobId}`, jobData);
    return response.data;
  },

  /**
   * Delete a job
   */
  deleteJob: async (jobId: string): Promise<void> => {
    await apiClient.delete(`/api/v2/web/jobs/${jobId}`);
  },

  /**
   * Get all job runs
   */
  getAllJobRuns: async (): Promise<JobRun[]> => {
    const response = await apiClient.get<JobRun[]>('/api/v2/web/job-logs');
    return response.data;
  },

  /**
   * Get detailed logs for a specific job run
   */
  getJobRunDetail: async (runId: string): Promise<JobLogDetail> => {
    const response = await apiClient.get<JobLogDetail>(`/api/v2/web/job-logs/${runId}/detail`);
    return response.data;
  }
};

export type { Job, JobFormData, JobRun, JobLogEntry, JobLogDetail };
